import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatLongDateTime, formatNumber } from './format';

const TABS = [['pallet', 'Pallets'], ['bin', 'Bins'], ['all', 'All']];

export const pctText = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

export const barColor = (pct) => (pct > 0.85 ? 'bg-red-400' : pct > 0.6 ? 'bg-orange-400' : 'bg-emerald-400');

export const levelLabel = (row) => (row.level ? `Level ${row.level}` : 'No level');

export const useLocationCapacity = ({ enabled }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/v1/warehouse/capacity');
      setData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to load location capacity');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);
  return { data, loading, error, refresh };
};

const Kpi = ({ label, bucket }) => (
  <div className="rounded-md border border-gray-200 px-2.5 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{label}</p>
    <p className="mt-0.5 text-lg font-semibold text-gray-900">{pctText(bucket?.pct)}</p>
    <p className="text-[11px] text-gray-500">{formatNumber(bucket?.occupied)} / {formatNumber(bucket?.total)} · {formatNumber(bucket?.empty)} empty</p>
  </div>
);

const Row = ({ label, sub, bucket, onClick }) => {
  const content = (
    <>
      <div className="w-24 shrink-0">
        <p className="text-xs font-semibold text-gray-900">{label}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
      <div className="h-2 flex-1 overflow-hidden rounded bg-gray-100"><div className={`h-full ${barColor(bucket.pct)}`} style={{ width: `${Math.min(100, bucket.pct * 100)}%` }} /></div>
      <span className="w-10 text-right text-xs font-semibold text-gray-900">{pctText(bucket.pct)}</span>
      <span className="w-20 text-right text-[11px] text-gray-500">{formatNumber(bucket.occupied)} / {formatNumber(bucket.total)}</span>
    </>
  );
  return onClick
    ? <li><button type="button" onClick={onClick} className="flex w-full items-center gap-2 rounded px-1 py-1.5 text-left hover:bg-gray-50">{content}</button></li>
    : <li className="flex items-center gap-2 px-1 py-1.5">{content}</li>;
};

const Section = ({ title, children }) => (
  <section className="mb-3">
    <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">{title}</h4>
    <ul className="divide-y divide-gray-100">{children}</ul>
  </section>
);

const REASON_TEXT = {
  capacity_family_missing: 'no pallet size in the Capacity Matrix',
  capacity_not_governed: 'no pallet size in the Capacity Matrix',
  manual_capacity: 'pallet size is manual',
  capacity_governance: 'pallet size not approved yet',
};

const Inbound = ({ inbound }) => {
  if (!inbound?.shipments?.length) return <p className="text-[11px] text-gray-500">No open POs with shipped, unreceived units.</p>;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-gray-700">
      <p className="font-semibold text-gray-900">Shipped, not received: {formatNumber(inbound.units)} units · about {formatNumber(inbound.spots)} pallet spots</p>
      <ul className="mt-1 space-y-0.5">
        {inbound.shipments.map((shipment) => (
          <li key={`${shipment.po_number}-${shipment.container}`}>
            PO {shipment.po_number}{shipment.container ? ` · ${shipment.container}` : ''} · {formatNumber(shipment.units)} units · ~{formatNumber(shipment.spots)} spots
            {shipment.unsized.length > 0 && <span className="text-gray-500"> ({formatNumber(shipment.unsized.reduce((sum, item) => sum + item.units, 0))} units have no pallet size)</span>}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center gap-2">
        <span className="font-semibold text-gray-900">Pallets after</span>
        <div className="h-2 flex-1 overflow-hidden rounded bg-white"><div className={`h-full ${barColor(inbound.palletAfter.pct)}`} style={{ width: `${Math.min(100, inbound.palletAfter.pct * 100)}%` }} /></div>
        <span className="font-semibold text-gray-900">{pctText(inbound.palletAfter.pct)}</span>
        <span className="text-gray-500">{formatNumber(inbound.palletAfter.occupied)} / {formatNumber(inbound.palletAfter.total)}</span>
      </div>
      {inbound.unsizedUnits > 0 && (
        <p className="mt-1 text-gray-500">
          Not in the estimate: {[...new Map(inbound.shipments.flatMap((shipment) => shipment.unsized).map((item) => [item.sku, item])).values()].slice(0, 6).map((item) => `${item.sku} (${REASON_TEXT[item.reason] || item.reason})`).join(', ')}
        </p>
      )}
      <p className="mt-1 text-gray-500">Spots = units ÷ the SKU family&apos;s pallet max in the Capacity Matrix, rounded up per SKU.</p>
    </div>
  );
};

const CapacityPanel = ({ data, loading, error, runs = [], onSelectRack, onRefresh, onClose }) => {
  const [tab, setTab] = useState('pallet');
  const syncedAt = data?.inventorySyncedAt || data?.shipheroSync?.last_update;
  return (
    <aside className="absolute inset-y-0 left-0 z-30 flex w-[400px] flex-col border-r border-gray-200 bg-white shadow-xl">
      <div className="flex items-start justify-between border-b border-gray-200 px-3 py-2.5">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Location capacity</p>
          <h3 className="mt-0.5 text-[16px] font-semibold text-gray-900">{data ? `${pctText(data.all.pct)} full · ${formatNumber(data.all.occupied)} of ${formatNumber(data.all.total)} locations` : 'Loading…'}</h3>
          <p className="mt-0.5 text-xs text-gray-500">From ShipHero · synced {formatLongDateTime(syncedAt)}</p>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onRefresh} aria-label="Refresh capacity" className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
          <button type="button" onClick={onClose} aria-label="Close capacity" className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="border-b border-gray-200 px-3 py-2">
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300" role="tablist">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)} className={`px-3 py-1 text-xs font-medium ${tab === key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {error && <p className="text-xs text-red-600">{error}</p>}
        {data && (
          <>
            <div className="mb-3 grid grid-cols-3 gap-2">
              <Kpi label="Pallets" bucket={data.pallet} />
              <Kpi label="Bins" bucket={data.bin} />
              <Kpi label="All" bucket={data.all} />
            </div>
            {tab === 'pallet' && (
              <>
                <Section title="Pallet spots by level">
                  {data.palletByLevel.map((row) => <Row key={row.key} label={levelLabel(row)} sub={row.deepReserve ? 'Deep reserve' : null} bucket={row} />)}
                </Section>
                <Section title="Pallet spots by rack">
                  {data.palletByRun.map((row) => {
                    const run = runs.find((item) => item.id === row.key);
                    return <Row key={row.key} label={row.label} sub={row.key} bucket={row} onClick={run && onSelectRack ? () => onSelectRack(run) : null} />;
                  })}
                </Section>
                <section className="mb-3">
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">After inbound</h4>
                  <Inbound inbound={data.inbound} />
                </section>
              </>
            )}
            {tab === 'bin' && (
              <Section title="Bins by area">
                {data.binByArea.map((row) => <Row key={row.key} label={row.key} sub={row.run} bucket={row} />)}
              </Section>
            )}
            {tab === 'all' && (
              <Section title="By type">
                <Row label="Pallets" bucket={data.pallet} />
                <Row label="Bins" bucket={data.bin} />
              </Section>
            )}
          </>
        )}
      </div>
      <div className="border-t border-gray-200 px-3 py-2 text-[11px] text-gray-500">
        A spot counts as full when ShipHero shows any stock in it. Not counted: {formatNumber(data?.excluded?.sparePool)} &quot;USE FIRST&quot; spare names and {formatNumber(data?.excluded?.noFixedSpot)} spots with no fixed place. Levels come from the validated rack map.
      </div>
    </aside>
  );
};

export default CapacityPanel;
